import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { LoginAsComponent } from 'src/app/pages/login/login-as/login-as.component';
import { AccountService } from 'src/app/shared/services/account.service';
import { JobsService } from 'src/app/shared/services/jobs.service';
import { ModalDialogService } from 'src/app/shared/services/modal-dialog.service';
import { ConfirmAlertButton, PromptsService } from 'src/app/shared/services/prompts.service';
import { StorageService } from 'src/app/shared/services/storage.service';

@Injectable({
  providedIn: 'root',
})
export class UserPermissionValidationService {
  constructor(
    private navCtrl: NavController,
    private accountService: AccountService,
    private storage: StorageService,
    public jobsService: JobsService,
    private promptsService: PromptsService,
    private modalDialogService: ModalDialogService,
  ) {}

  /**
   * Post login handle validations for different account users.
   */
  handlePostLoginValidations(): Promise<boolean> {
    // When a SP(approved) logs in to mobile app for first time, automatically mark them as Available for LMJ
    const currentUser = this.accountService?.getUser()?.custom;
    if (this.accountService.userIs('carer') && currentUser?.approved && currentUser?.first_mobile_login) {
      this.setFlexAvailability(true);
    }
    return this.handleOthers();
  }

  private showLoginAsModal(): void {
    this.modalDialogService.openDialog({ component: LoginAsComponent }).subscribe((response) => {
      if (response.data) {
        this.dismiss();
      }
    });
  }

  private dismiss(): Promise<any> {
    // Need to use navCtrl instead of router
    // because router doesnt recreate home page
    // and uses old copy of home page
    return this.navCtrl.navigateRoot('tabs/home');
  }

  private async handleOthers(): Promise<boolean> {
    if (this.accountService.userIs('consumer') || this.accountService.userIs('carer')) {
      this.dismiss();
      return Promise.resolve(true);
    }

    if (this.accountService.userIs('Admin')) {
      this.showLoginAsModal();
      return Promise.resolve(true);
    }

    this.displayRestrictedNotice();
    return Promise.resolve(false);
  }

  /**
   * Display notice for the coordinator login
   */
  private displayRestrictedNotice(): void {
    const confirmAlertActions: [ConfirmAlertButton] = [
      {
        text: 'OK',
        handler: () => {
          this.logout();
        },
      },
    ];
    this.promptsService.showConfirmAlert({
      header: 'Notice',
      message: 'This app is currently restricted to care workers and clients',
      buttons: confirmAlertActions,
    });
  }

  /**
   * Set Flex Availability
   *
   * @param flexValue
   */
  private setFlexAvailability(flexValue: boolean): void {
    this.jobsService
      .setUserAvailability(flexValue)
      .then(() => {
        this.storage.ready().then(() => this.storage.set('flex_enabled', flexValue));
      })
      .catch((error) => {
        console.error(error);
      });
  }

  private logout(): void {
    this.navCtrl.navigateRoot('/logout', { queryParams: { trigger: 'login page other account logged in' } });
  }
}
